import { useState, useEffect, useCallback } from "react";
import { Plus, X } from "lucide-react";
import { cn } from "../../lib/utils";
import FormField from "../ui/FormField";
import { Button } from "../ui/Button";
import RichTextEditor from "../ui/RichTextEditor";
import { SunbedsTable, SunbedRoom } from "../../components/sunbeds/SunbedsTable";
import {
  createSunbedRoom,
  getSunbedRooms,
  updateSunbedRoom,
  deleteSunbedRoom
} from "../../services/sunbeds";
export interface PricingOption {
  id: string;
  label: string;
  type: "per_minute" | "fixed";
  /** String while editing (e.g. "0."), number after normalise on save. */
  price: number | string;
  duration: number;
}

function isValidPriceDraft(value: string): boolean {
  return value === "" || /^\d*\.?\d*$/.test(value);
}

function formatPriceForInput(price: number | string): string {
  if (typeof price === "string") return price;
  return price === 0 ? "" : String(price);
}

function parsePriceForSubmit(price: number | string): number | null {
  const raw = String(price).trim();
  if (raw === "" || raw === ".") return null;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
}

export interface PackageFormInput {
  id?: string;
  label: string;
  description?: string;
  status: "active" | "inactive";
  // Can be a list of room ids (string) or detailed room objects from API
  sunbed_room: (string | SunbedRoom)[];
  pricing: PricingOption[];
}

interface AddPackageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: PackageFormInput) => Promise<void>;
  existingPackage?: PackageFormInput | null;
  mode: "add" | "edit";
}

type FieldErrors = Record<string, string>;
export const AddPackageModal = ({
  isOpen,
  onClose,
  onSave,
  existingPackage,
  mode,
}: AddPackageModalProps) => {
  const [rooms, setRooms] = useState<SunbedRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<"active" | "inactive">("active");
  const [selectedRooms, setSelectedRooms] = useState<string[]>([]);
  const [pricing, setPricing] = useState<PricingOption[]>([]);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  // Fetch Rooms from API
  const fetchRooms = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getSunbedRooms();
      if (response && response.data) {
        // Map API response to UI model
        const mappedRooms: SunbedRoom[] = response.data.map((item: any) => ({
          id: item.id.toString(),
          label: item.label_formatted || item.label,
          status: (item.availability_formatted || item.availability) as "Available" | "In-session" | "Maintenance" | "Cleaning",
          totalDuration: item.total_hours_formatted || "0 mins"
        }));
        setRooms(mappedRooms);
      }
    } catch (error) {
      console.error("Failed to fetch sunbed rooms", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setFieldErrors({});
      setFormError(null);
      setIsSaving(false);
      if (existingPackage && mode === "edit") {        setLabel(existingPackage.label);
        setDescription(existingPackage.description || "");
        setStatus(existingPackage.status);
        // Normalise existing package rooms into a list of labels for selection
        setSelectedRooms(
          (existingPackage.sunbed_room || []).map((room) =>
            typeof room === "string" ? room : room.label
          )
        );
        setPricing(
          existingPackage.pricing.map((p) => ({
            ...p,
            price: formatPriceForInput(p.price),
          }))
        );
      } else {
        setLabel("");
        setDescription("");
        setStatus("active");
        setSelectedRooms([]);
        setPricing([]);
      }
      fetchRooms();
    }
  }, [isOpen, existingPackage, mode, fetchRooms]);

  const handleAddPricing = () => {
    setPricing([
      ...pricing,
      {
        id: Date.now().toString(),
        label: "",
        type: "per_minute",
        price: "",
        duration: 0,
      },
    ]);
  };

  const handleUpdatePricing = (
    id: string,
    field: keyof PricingOption,
    value: string | number
  ) => {
    if (field === "price" && typeof value === "string" && !isValidPriceDraft(value)) {
      return;
    }
    setPricing(
      pricing.map((p) => (p.id === id ? { ...p, [field]: value } : p))
    );
  };

  const handleRemovePricing = (id: string) => {
    setPricing(pricing.filter((p) => p.id !== id));
  };

  const toggleRoom = (roomLabel: string) => {
    setSelectedRooms((prev) =>
      prev.includes(roomLabel)
        ? prev.filter((r) => r !== roomLabel)
        : [...prev, roomLabel]
    );
  };

  const clearFieldError = (key: string) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validateForm = (
    normalisedPricing: PricingOption[],
    selectedRoomIds: string[],
  ): FieldErrors => {
    const errors: FieldErrors = {};

    if (!label.trim()) {
      errors.label = "Package label is required.";
    }

    if (selectedRoomIds.length === 0) {
      errors.sunbed_room =
        selectedRooms.length > 0
          ? "Selected rooms could not be matched — refresh and try again."
          : "Select at least one sunbed room for this package.";
    }

    if (normalisedPricing.length === 0) {
      errors.pricing = "Add at least one pricing option.";
    }

    pricing.forEach((p) => {
      if (!p.label.trim()) {
        errors[`pricing-${p.id}-label`] = "Price label is required.";
      }
      const priceNum = parsePriceForSubmit(p.price);
      if (priceNum === null) {
        errors[`pricing-${p.id}-price`] =
          "Enter a valid price (0 or greater).";
      }
      if (p.type === "fixed" && (!p.duration || p.duration < 1)) {
        errors[`pricing-${p.id}-duration`] =
          "Fixed pricing requires duration (minutes) of at least 1.";
      }
    });

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const normalisedPricing: PricingOption[] = [];
    for (const p of pricing) {
      const priceNum = parsePriceForSubmit(p.price);
      normalisedPricing.push({
        ...p,
        price: priceNum ?? p.price,
      });
    }

    const selectedRoomIds = selectedRooms
      .map((roomLabel) => rooms.find((r) => r.label === roomLabel)?.id)
      .filter((id): id is string => Boolean(id));

    const errors = validateForm(normalisedPricing, selectedRoomIds);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const missing: string[] = [];
      if (errors.label) missing.push("package label");
      if (errors.sunbed_room) missing.push("at least one room");
      if (errors.pricing) missing.push("at least one pricing row");
      const pricingDetail = Object.keys(errors).some((k) => k.startsWith("pricing-"));
      if (pricingDetail) missing.push("complete pricing details (label, price, duration)");
      setFormError(
        `Missing or invalid: ${missing.join(", ")}.`,
      );
      return;
    }

    const conditionalPricing = normalisedPricing.map((p) => {
      const priceNum = parsePriceForSubmit(p.price)!;
      const row = { ...p, price: priceNum };
      if (p.type === "fixed") {
        return row;
      }
      const { duration, ...rest } = row;
      return rest as PricingOption;
    });

    setIsSaving(true);
    try {
      await onSave({
        label: label.trim(),
        description,
        status,
        sunbed_room: selectedRoomIds,
        pricing: conditionalPricing,
      });
      onClose();
    } catch {
      // Parent shows API errors; keep modal open
    } finally {
      setIsSaving(false);
    }
  };
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/40 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#885ABB] flex justify-between items-center bg-[#885ABB] rounded-t-xl">
          <h2 className="text-xl font-bold text-white">
            {mode === "add" ? "Add Package" : "Edit Package"}
          </h2>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white hover:bg-white/10 p-1 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto px-6 py-6 space-y-6"
        >
          {formError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {formError}
            </div>
          )}

          <div className="grid grid-cols-2 gap-6">
            <FormField
              label="Label *"
              name="label"
              value={label}
              onChange={(_, val) => {
                setLabel(val);
                clearFieldError("label");
              }}
              placeholder="Package Name"
              error={fieldErrors.label}
            />
            <FormField
              label="Status *"
              name="status"
              type="select"
              value={status}
              onChange={(_, val) => {
                setStatus(val as "active" | "inactive");
              }}
              options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }]}
              placeholder="Select status"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-[#402B69]">
              Description
            </label>
            <RichTextEditor
              value={description}
              onChange={setDescription}
              placeholder="Describe this sunbed package..."
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-[#402B69]">
              Sunbeds (Rooms) *
            </label>
            <div
              className={cn(
                "min-h-[50px] rounded-lg border bg-[#F9F9F9] px-4 py-3",
                fieldErrors.sunbed_room ? "border-red-300" : "border-gray-200",
              )}
            >
              {loading ? (
                <p className="text-sm text-gray-500">Loading rooms...</p>
              ) : rooms.length === 0 ? (
                <p className="text-sm text-amber-700">
                  No sunbed rooms found. Create rooms under Sunbeds → Rooms first.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {rooms.map((room) => (
                    <button
                      key={room.id}
                      type="button"
                      onClick={() => {
                        toggleRoom(room.label);
                        clearFieldError("sunbed_room");
                      }}
                      className={cn(
                        "px-3 py-1 text-sm rounded-full transition-colors border",
                        selectedRooms.includes(room.label)
                          ? "bg-[#F3E8FF] border-[#C4A6E1] text-[#885ABB] font-medium"
                          : "bg-white border-gray-200 text-gray-600 hover:border-[#C4A6E1]",
                      )}
                    >
                      {room.label}
                    </button>
                  ))}
                </div>
              )}
              {selectedRooms.length === 0 && rooms.length > 0 && !fieldErrors.sunbed_room && (
                <p className="text-sm text-gray-400 mt-1">Select rooms to apply to this package</p>
              )}
            </div>
            {fieldErrors.sunbed_room && (
              <p className="text-xs text-red-600">{fieldErrors.sunbed_room}</p>
            )}
          </div>

          {/* Pricing Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 pb-2">
              <h3 className="font-semibold text-[#402B69]">Pricing *</h3>
              <Button
                type="button"
                onClick={() => {
                  handleAddPricing();
                  clearFieldError("pricing");
                }}
                variant="outline"
                className="text-[#885ABB] border-[#885ABB] hover:bg-[#F3E8FF]"
              >
                + Add Pricing
              </Button>
            </div>
            {fieldErrors.pricing && (
              <p className="text-xs text-red-600">{fieldErrors.pricing}</p>
            )}

            <div className="space-y-3">
              {pricing.map((p) => (
                <div
                  key={p.id}
                  className="flex gap-4 items-end bg-gray-50 p-3 rounded-lg border border-gray-100"
                >
                  <div className="flex-1">
                    <FormField
                      label="Price Label *"
                      name={`price-label-${p.id}`}
                      value={p.label}
                      onChange={(_, val) => {
                        handleUpdatePricing(p.id, "label", val);
                        clearFieldError(`pricing-${p.id}-label`);
                      }}
                      placeholder="e.g. Standard"
                      error={fieldErrors[`pricing-${p.id}-label`]}
                    />
                  </div>
                  <div className="w-[130px]">
                    <FormField
                      label="Type *"
                      name={`price-type-${p.id}`}
                      type="select"
                      value={p.type}
                      onChange={(_, val) =>
                        handleUpdatePricing(p.id, "type", val)
                      }
                      options={[
                        { value: "per_minute", label: "Per minute" },
                        { value: "fixed", label: "Fixed" },
                      ]}
                      placeholder="Select Type"
                    />
                  </div>
                  {p.type === "fixed" && (
                    <div className="w-[100px]">
                      <FormField
                        label="Duration *"
                        name={`price-duration-${p.id}`}
                        value={p.duration?.toString() || "0"}
                        onChange={(_, val) => {
                          handleUpdatePricing(p.id, "duration", parseFloat(val) || 0);
                          clearFieldError(`pricing-${p.id}-duration`);
                        }}
                        placeholder="Mins"
                        error={fieldErrors[`pricing-${p.id}-duration`]}
                      />
                    </div>
                  )}
                  <div className="w-[100px]">
                    <FormField
                      label="Price *"
                      name={`price-val-${p.id}`}
                      value={formatPriceForInput(p.price)}
                      onChange={(_, val) => {
                        handleUpdatePricing(p.id, "price", val);
                        clearFieldError(`pricing-${p.id}-price`);
                      }}
                      placeholder="0.00"
                      error={fieldErrors[`pricing-${p.id}-price`]}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => handleRemovePricing(p.id)}
                    className="mb-[2px] text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    Remove
                  </Button>
                </div>
              ))}
              {pricing.length === 0 && (
                <p className="text-sm text-gray-400 italic">
                  No pricing added yet. Click &quot;+ Add Pricing&quot; to add at least one option.
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <Button
              variant="outline"
              onClick={onClose}
              type="button"
              disabled={isSaving}
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving || loading}
              className="bg-[#885ABB] hover:bg-[#6f47a2] text-white"
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};